import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { ConnectionStatus, Network } from "@capacitor/network";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, interval, Observable, of, Subscription } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
  timeout,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { Plant } from "src/model/class/general classes/plant";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import {
  ApplicationTheme,
  ToastConnectionCodes,
} from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { NetworkAddressCheck } from "src/model/interface/INetworkAddressCheck";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { PlantService } from "./plant.service";
import { ThemeService } from "./theme.service";
import { ToastService } from "./toast.service";
import moment from "moment";


@Injectable({
  providedIn: "root",
})
export class NetworkService implements OnDestroy, iTranslation, iEvent {
  private _logContext: string = "Network.service";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectSelectedPlantUserSub: Subscription = null;
  private _selectSelectedBuildingUserSub: Subscription = null;
  private _userIsAuthenticatedSubrcitption: Subscription = null;
  //#endregion

  private _listAddressCheck: Array<NetworkAddressCheck> =
    new Array<NetworkAddressCheck>();
  private _plantToTest: Plant = null;
  private _buildingToTest: Building = null;
  private _connectionAttempWan: number = 0;
  private _isUserAuthenticated = false;
  private _currentConnectionType: ConnectionType = ConnectionType.NONE;
  private _currentConnectionTypeSubject: BehaviorSubject<ConnectionType> =
    new BehaviorSubject<ConnectionType>(null);
  private _disconnectionCountAttemps: number = 0;
  private _translateSubscription: Subscription = null;
  private _intervalHeartbit: number = 5000;
  private _isTimerConnectionRunning: boolean = false;

  /**Flag value; if the first polling cycle "0" (caused by the change of the "selectedPlant") is completed, it is set to TRUE; othervise,
   * it is set to FALSE.
   *
   * This flag is useful in order to make sure the two possibiles circumstances in which the polling cycle is re-started:
   *
   * --Invocation of "initializeEvents()" due to changes in the "selectedPlant"
   *
   * --Firing of "attachMobileNetworkEvent()" due to change of network-type on MOBILE DEVICE.
   *
   * NEVER overlaps.
   */
  private _isPollingcycle0completed: boolean = false;

  private _heart: Observable<number> = null;
  /**Subscription to "_heart" pulsation emitter */
  private _surgeonSubscription: Subscription = null;

  //#region Translated string
  private _lblNoConnectionAvaiableRetrying: string = "";
  //#endregion

  /**Flag property; if the service has re-estabilished a connection after a period of connectionlessness, it is set to true;
   * otherwise, it is set to false.
   */
  private _backFromDead: boolean = false;

  public readonly currentConnectionType =
    this._currentConnectionTypeSubject.asObservable();

  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _platform: Platform,
    private _http: HttpClient,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _plantService: PlantService,
    private _translateService: TranslateService,
    private _toastService: ToastService,
    private _ngZone: NgZone,
    private _loggerService: LoggerService,
    private _store: Store,
    private _themeService: ThemeService
  ) {
    /*if (this.isMobileDevice()) {
      this.attachMobileNetworkEvent();
    }*/

    this._userIsAuthenticatedSubrcitption = this._store
      .select(isUserAuthenticated)
      .subscribe((isUserAuthenticated: boolean) => {
        this._isUserAuthenticated = isUserAuthenticated;
      });

    this.subscribeEvents();

    this._heart = interval(this._intervalHeartbit);

    this._selectSelectedBuildingUserSub = this._store
      .select(selectedBuilding)
      .pipe(
        map((currentBuilding: Building) => {
          if (currentBuilding) {
            //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_plantService", "selectedPlant", "Currently selected plant is: "+currentPlant.PlantName);
            currentBuilding = new Building(currentBuilding);
          }
          return currentBuilding;
        })
      )
      .subscribe((currentBuilding: Building) => {
        this._buildingToTest = currentBuilding;

        if (currentBuilding) {
          this.setCurrentConnectionType(ConnectionType.WAN);
          this.initializeEvents();
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();

    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "REACT"
        );

        this._backFromDead = false;
        //this._plantToTest.changesBlocked = false;

        this.cancelRunningHeartbitTimer();
        this.resetNoConnectionAvaiableVariable();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnComebackToPlantList.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnComebackToPlantList",
          "REACT"
        );
        //this.cancelRunningHeartbitTimer();

        this._plantToTest.changesBlocked = false;

        /* This causes the arrest of polling-cycle */
        this._plantService.deselectCurrentPlant();
        this.resetNoConnectionAvaiableVariable();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnComebackToPlantSettings.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnComebackToPlantSettings",
          "REACT"
        );

        this._plantToTest.changesBlocked = false;

        this.cancelRunningHeartbitTimer();
        this.resetNoConnectionAvaiableVariable();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Return URI by ip and port
   *
   * @param isHTTPS Flag enable https comunications
   * @param ip IP to get URI
   * @param port Port to get URI
   * @returns
   */
  public getGenericURIByIPandPort(
    isHTTPS: boolean,
    ip: string,
    port: string
  ): string {
    let uri: string = "http://" + ip + ":" + port;

    if (isHTTPS) {
      uri = "https://" + ip + ":" + port;
    }

    return uri;
  }

  /**
   * Send heartbit to specific IP configuration to test reacheable
   *
   * @param ipConnectionString Full IP to send heartbit connection
   * @param timeoutTime Timeout to apply to send heartbi
   * @returns
   */
  public async sendServerHeartbit(
    ipConnectionString: string,
    timeoutTime: number = 10000
  ): Promise<boolean> {
    let testSendTimeStamp: string = moment().format("YYYYMMDDHHmmss");
    let isReachable: boolean = false;

    const timeOutSignal = new AbortController();
    const timeOutDef = setTimeout(() => timeOutSignal.abort(), timeoutTime);

    try {
      const response = await fetch(
        ipConnectionString +
        AppSettings.API_URL_SYSTEM +
        "/test?q=" +
        testSendTimeStamp,
        {
          method: "GET",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
          signal: timeOutSignal.signal,
        }
      );

      await response.json();

      isReachable = true;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "sendServerHeartbit()",
        "Timeout: " +
        timeoutTime +
        " | ERROR: " +
        (exception?.message ?? JSON.stringify(exception))
      );
      isReachable = false;
    } finally {
      clearTimeout(timeOutDef);
    }

    return isReachable;
  }

  /**
   * This method handle the service reaction to a plant in "Digital Place Mode"; since NO POLLING is requested, it shall check each addresses contained in
   * the plant ONCE, until a operable address is found OR the list is exausted.
   *
   * If a connection is estabilished, that address is used to set "environment.httpServerRoot" and the appropriate connection type is set.
   * @returns TRUE if a connection is found, FALSE otherwise.
   */
  public async handleDigitalPlaceCheckConnectionProcedure(): Promise<boolean> {
    let isSomeConnectionAvaiable: boolean = false;
    let address: string = "";

    //#region Define which addresses (LAN or WAN) are present in the current plant.
    const isSpecificLanConnectionAvaiable =
      this._listAddressCheck.find(
        (connection: NetworkAddressCheck) =>
          connection.connectionType === ConnectionType.LAN
      ) !== undefined;
    const isSpecificWanConnectionAvaiable =
      this._listAddressCheck.find(
        (connection: NetworkAddressCheck) =>
          connection.connectionType === ConnectionType.WAN
      ) !== undefined;
    const isWanLanConnectionAvaiable =
      this._listAddressCheck.find(
        (connection: NetworkAddressCheck) =>
          connection.connectionType === ConnectionType.LANWAN
      ) !== undefined;
    //#endregion

    try {
      if (isSpecificLanConnectionAvaiable) {
        isSomeConnectionAvaiable = await this.sendServerHeartbit(
          environment.httpServerRoot,
          AppSettings.DEFAULT_LAN_TIMEOUT_MS
        );
      }
      if (isSomeConnectionAvaiable) {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleDigitalPlaceCheckConnectionProcedure()",
          "AFTER heartbit with LAN"
        );
        this.setCurrentConnectionType(ConnectionType.LAN);

        address = this._plantToTest.FullLanAddress;
      } else if (
        !isSomeConnectionAvaiable &&
        (isSpecificWanConnectionAvaiable || isWanLanConnectionAvaiable)
      ) {
        isSomeConnectionAvaiable = await this.sendServerHeartbit(
          environment.httpServerRoot,
          AppSettings.DEFAULT_WAN_TIMEOUT_MS
        );

        if (isSomeConnectionAvaiable) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "handleDigitalPlaceCheckConnectionProcedure()",
            "AFTER heartbit with WAN"
          );

          if (isWanLanConnectionAvaiable) {
            this.setCurrentConnectionType(ConnectionType.LANWAN);
          } else {
            this.setCurrentConnectionType(ConnectionType.WAN);
          }

          address = environment.httpServerRoot;
        }
      }

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleDigitalPlaceCheckConnectionProcedure()",
        "final address: " + address
      );

      if (environment.httpServerRoot !== address) {
        environment.httpServerRoot = address;

        this._eventService.OnServerRootChanged.next(address);
      }
    } catch (ex) { }

    return isSomeConnectionAvaiable;
  }

  //#region Translation
  /**
   * Load translation from translation service
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.noConnectionAvaiableRetrying"])
      .subscribe((translation) => {
        this._lblNoConnectionAvaiableRetrying =
          translation["dictionary.noConnectionAvaiableRetrying"];
      });
  }

  /**
   * Subscribe translation to translation event
   */
  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectSelectedPlantUserSub) {
      this._selectSelectedPlantUserSub.unsubscribe();
    }
    if (this._userIsAuthenticatedSubrcitption) {
      this._userIsAuthenticatedSubrcitption.unsubscribe();
    }
  }
  //#endregion

  /**
   * This method applies different filter conditions on the "currentPlant" read from Store, based on the peculiar "oprative mode" associate dwith the current plant.
   * @param currentPlant
   * @returns
   */
  private filterCondition(currentPlant: Plant): boolean {
    let solution = true;

    /*NOTE: If "CurrentPlant" is nullish, then we must PROCEED, since this happening is signal to stop the polling-cycle (if this is running)  */
    if (currentPlant) {
      switch (currentPlant.operativeMode) {
        case BIGDeviceOperationMode.STANDARD:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "filterCondition()",
            "STANDARD"
          );
          solution = true;
          break;

        case BIGDeviceOperationMode.ACCESS_CONTROL_MODE:
        case BIGDeviceOperationMode.DIGITAL_PLACE_MODE:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "filterCondition()",
            "ACCESS_CONTROL_MODE || DIGITAL_PLACE_MODE"
          );
          /*NOTE: If "_plantToTest" is nullish, then we must PROCEED. This is signal that we are currently
           ** "logging in" to a new plant (manually OR through "automatic-login") --->We shall later be hadled by invocation of "handleNotStandardMode(...)"
           ** Entering into "Plant.page --->We shall be later blocked by "changesBlocked"
           */
          if (!this._plantToTest) {
            solution = true;
          } else {
            /*NOTE: If "_plantToTest" is defined, then we must BE BLOCKED, UNLESS new "currentPlant" read from store is SUBSTANTIALLY DIFFERENT from the one previsuosly read.
            This is determined based on the value of "EndPoint" property (setted based on "htppServerRoot" global variable)  */
            solution = this._plantToTest.Endpoint !== currentPlant.Endpoint;
          }
          break;

        default:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "filterCondition()",
            "OTHER"
          );

          solution = true;
          break;
      }
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "filterCondition()",
      "CONTINUE: " + solution
    );

    return solution;
  }

  /**
   * THis method handles the peculiar behaviuor of the service when the app is NOT running on "STANDARD" mode.
   */
  private handleNotStandardMode(currentOperativeMode: BIGDeviceOperationMode) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleNetworkWhileStandAlone()",
      "NO STANDARD"
    );

    switch (currentOperativeMode) {
      case BIGDeviceOperationMode.ACCESS_CONTROL_MODE:
        //this._plantToTest.changesBlocked = true;

        //Set artificially "current connection type" as "LANWAN" (we simulate the situation where both LAN and WAN IPAddress/gates are equal).
        //This is useful, aming other things, to control the graphical components' color.
        this.setCurrentConnectionType(ConnectionType.LANWAN);

        environment.httpServerRoot = this._plantToTest.FullLanAddress;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleNetworkWhileStandAlone()",
          "new httpServerRoot: " + environment.httpServerRoot
        );

        this._eventService.OnServerRootChanged.next(
          this._plantToTest.FullLanAddress
        );

        break;

      case BIGDeviceOperationMode.DIGITAL_PLACE_MODE:
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleNetworkWhileStandAlone()",
          "DIGITAL_PLACE_MODE"
        );

        this.receiveExternalChanges();

        this.handleDigitalPlaceCheckConnectionProcedure();

        break;

      default:
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleNotStandardMode",
          "Operative mode: OTHER NOT-STANDRD"
        );

        break;
    }
  }

  /**
   * This method performs actions which are needed when changes are applied to current plant, BUT ONLY IF that
   * HeartbitTimer is NOT running.
   *
   */
  private async initializeEvents() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "initializeEvents()",
      "is TimerConnectionRunning: " + this._isTimerConnectionRunning
    );

    if (!this._isTimerConnectionRunning) {
      this._connectionAttempWan = 0;
      this._disconnectionCountAttemps = 0;

      await this.startTimerHeartbitHandling();
    }
  }

  /**
   * This method performs actions which are needed when changes are applied to current plant, REGARDLESS that
   * HeartbitTimer is running or not.
   */
  private async receiveExternalChanges(): Promise<void> {
    this.populateAddressToCheck();
  }

  /**
   * Delete current heartbit check timer
   */
  private cancelRunningHeartbitTimer(): void {
    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "cancelRunningHeartbitTimer()", "START");
    this._isTimerConnectionRunning = false;

    if (this._surgeonSubscription !== null) {
      this._surgeonSubscription.unsubscribe();
    }

    this._disconnectionCountAttemps = 0;
    this.closeLoaderReconnectionInProgress();
  }

  /**
   * Handle timer who check ip connection state.
   * It delete current pending heartbit timer
   */
  private async startTimerHeartbitHandling(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "startTimerHeartbitHandling()",
      "START"
    );

    this.cancelRunningHeartbitTimer();

    this._isTimerConnectionRunning = true;

    this._isPollingcycle0completed = false;

    await this.handleTimerTestConnection();

    this._isPollingcycle0completed = true;

    //await this.handleTimerTestConnection();

    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "startTimerHeartbitHandling()", "END OF CYCLE 0");

    //Definition of interval moved to constructor; subscription/unsubscription to the same moved to "startTimerHeartbitHandling()"/"cancelRunningHeartbitTimer()" method
    this._surgeonSubscription = this._heart
      .pipe(takeWhile(() => this._isTimerConnectionRunning))
      .subscribe(async () => {
        await this.handleTimerTestConnection();
      });
  }

  /**
   * Handle callback timer heartbit
   */
  private async handleTimerTestConnection() {
    let resultHeartbitCall: boolean = false;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleTimerTestConnection()",
        "INTERVAL (NS)- enviroment is: " + environment.httpServerRoot
      );

      if (environment.httpServerRoot !== "") {
        resultHeartbitCall = await this.sendServerHeartbit(
          environment.httpServerRoot,
          this.getCurrentEnvironmentAddressTimeout()
        );

        if (resultHeartbitCall) {
          // this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "handleTimerTestConnection()", "CURRENT CONNECTION TYPE: "+ConnectionType[this._currentConnectionType]);

          this.doActionAfterConnection(environment.httpServerRoot);

          //If the App is NOT running on a mobile device (Android or iOS), then we cannot exploit the ionic native network service
          //in order to check, when a WAN connection is used, if a LAN connection becomes available.
          //
          //SO, we shall do it personally.
          // if (
          //   !this.isMobileDevice() &&
          //   this._themeService.applicationTheme !=
          //   ApplicationTheme.DIGITAL_PLACE
          // ) {
          //   resultHeartbitCall = await this.testChangeWANtoLAN(
          //     resultHeartbitCall
          //   );
          // }
        }
      }
      //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "handleTimerTestConnection()", "INTERVAL (NS)- enviroment is: "+environment.httpServerRoot + " - RESULT HEARTBIT: " + resultHeartbitCall);

      /** In case of current endpoint is not reachable retest other connection */
      if (!resultHeartbitCall) {
        resultHeartbitCall = await this.reTestAllAvaiableConnection();
      }
    } catch (exception) {
      resultHeartbitCall = await this.reTestAllAvaiableConnection();
    } finally {
      if (!resultHeartbitCall) {
        this.handleNoAvaiableConnection();
      } else {
        this._disconnectionCountAttemps = 0;

        this.closeLoaderReconnectionInProgress();

        await this.closePopupDisconnectionHandling();
      }
    }
  }

  /**
   * This function check if it is possible to switch to a LAN network IN THE CASE that:
   * 1)The app is NOT running on a Android/iOS device
   * 2)The currently used network is a WAN network
   * It does so executing a test on all available connections
   * @param precedentResultHeatbeat Result of the just executed Heartbeat call
   * @returns The new result of the test on all the available connections
   */
  private async testChangeWANtoLAN(
    precedentResultHeatbeat: boolean
  ): Promise<boolean> {
    let resultHeartbitCall: boolean = precedentResultHeatbeat;

    //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "testChangeWANtoLAN()", "WE ARE NOT ON DEVICE");

    if (
      environment.httpServerRoot === this._plantToTest.FullWanAddress &&
      this._plantToTest.FullLanAddress !== this._plantToTest.FullWanAddress
    ) {
      this._connectionAttempWan++;

      //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "testChangeWANtoLAN()", "Attemp to switch to LAN n°: "+ this._connectionAttempWan);

      // maybe is just a flickering of VPN connection, so I'll try to connect to LAN network after few seconds
      if (this._connectionAttempWan % 5 === 0) {
        //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "testChangeWANtoLAN()", "NOW WE WILL TRY TO SWITCH TO LAN");

        resultHeartbitCall = await this.reTestAllAvaiableConnection();
        // I'll not set cont=0
      }

      /** Resetting attemp connection after 19 retry */
      if (this._connectionAttempWan === 19) {
        this._connectionAttempWan = 0;
      }
    }
    return resultHeartbitCall;
  }

  /**
   *  Check if some connection are avaiable, based on current connection type: Ethernet, WIFI,
   */
  private async reTestAllAvaiableConnection(): Promise<boolean> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "reTestAllAvaiableConnection()",
      "RETESTING ALL AVAILABLE CONNECTIONS"
    );

    let isSomeConnectionAvaiable: boolean = false;
    const isSpecificWanConnectionAvaiable =
      this._listAddressCheck.find(
        (connection: NetworkAddressCheck) =>
          connection.connectionType === ConnectionType.WAN
      ) !== undefined;
    const isWanLanConnectionAvaiable =
      this._listAddressCheck.find(
        (connection: NetworkAddressCheck) =>
          connection.connectionType === ConnectionType.LANWAN
      ) !== undefined;
    const currentNetworkType = (await Network.getStatus()).connectionType;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "reTestAllAvaiableConnection()",
      "LIST OF CONNECTIONS: "
    );
    for (const entry of this._listAddressCheck) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "reTestAllAvaiableConnection()",
        "LIST OF CONNECTIONS - ENTRY: " + JSON.stringify(entry)
      );
    }

    try {
      // Capacitor reports an ethernet connection as "wifi"
      if (
        currentNetworkType === "unknown" ||
        currentNetworkType === "wifi"
      ) {
        //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "reTestAllAvaiableConnection()", "TYPE: UNKNOWN OR WIFI OR ETHERNET");
        isSomeConnectionAvaiable =
          await this.handleDefualtCheckConnectionProcedure(
            isSpecificWanConnectionAvaiable
          );
      } else if (currentNetworkType === "none") {
        isSomeConnectionAvaiable = false;
      } else {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "reTestAllAvaiableConnection()",
          "MOBILE. Current network type: " + currentNetworkType
        );

        if (isWanLanConnectionAvaiable || isSpecificWanConnectionAvaiable) {
          let addressToUse: string = "";

          //Case of network smarthphone
          // if (isWanLanConnectionAvaiable) {
          //   addressToUse = this._plantToTest.FullLanAddress;
          // } else if (isSpecificWanConnectionAvaiable) {
          //   addressToUse = this._plantToTest.FullWanAddress;
          // }


          addressToUse = environment.httpServerRoot;
          isSomeConnectionAvaiable = await this.sendServerHeartbit(
            addressToUse,
            AppSettings.DEFAULT_WAN_TIMEOUT_MS
          );

          if (isSomeConnectionAvaiable) {
            this.doActionAfterConnection(addressToUse);

            this.setCurrentConnectionType(ConnectionType.WAN);
          }
        }
      }
    } catch (ex) { }

    return isSomeConnectionAvaiable;
  }

  /**
   * Handle default connection check for browser/PC/MYTO device.
   * It checks both lan and wan ip address(if avaiable)
   *
   * @param isSpecificWanConnectionAvaiable Specify if some wan address is avaiable
   * @returns Return if some connection ip is avaiable
   */
  private async handleDefualtCheckConnectionProcedure(
    isSpecificWanConnectionAvaiable: boolean
  ): Promise<boolean> {
    let isSomeConnectionAvaiable: boolean = false;
    let addressToUse: string = "";

    try {
      // addressToUse = this._plantToTest.FullLanAddress;

      addressToUse = environment.httpServerRoot;

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleDefualtCheckConnectionProcedure()",
        "BEFORE heartbit with LAN"
      );
      isSomeConnectionAvaiable = await this.sendServerHeartbit(
        addressToUse,
        AppSettings.DEFAULT_LAN_TIMEOUT_MS
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleDefualtCheckConnectionProcedure()",
        "isSomeConnectionAvaiable (LAN): " +
        isSomeConnectionAvaiable +
        " | isSpecificWanConnectionAvaiable: " +
        isSpecificWanConnectionAvaiable
      );

      if (isSomeConnectionAvaiable) {
        //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "handleDefualtCheckConnectionProcedure()", "AFTER heartbit with LAN");

        await this.doActionAfterConnection(addressToUse);
        this.setCurrentConnectionType(ConnectionType.LAN);
      } else if (!isSomeConnectionAvaiable && isSpecificWanConnectionAvaiable) {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleDefualtCheckConnectionProcedure()",
          "BEFORE heartbit with WAN"
        );


        addressToUse = environment.httpServerRoot;

        isSomeConnectionAvaiable = await this.sendServerHeartbit(
          addressToUse,
          AppSettings.DEFAULT_WAN_TIMEOUT_MS
        );

        if (isSomeConnectionAvaiable) {
          //this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "handleDefualtCheckConnectionProcedure()", "AFTER heartbit with WAN");

          await this.doActionAfterConnection(addressToUse);

          this.setCurrentConnectionType(ConnectionType.WAN);
        }
      }
    } catch (ex) { }

    return isSomeConnectionAvaiable;
  }

  /**
   * This function handle case where are at least one viable ip connection address
   *
   * @param address address to update
   */
  private async doActionAfterConnection(address: string) {
    await this.closePopupDisconnectionHandling();

    if (this._isTimerConnectionRunning) {
      if (environment.httpServerRoot !== address) {
        environment.httpServerRoot = address;

        this._eventService.OnServerRootChanged.next(address);
      }

      if (this._backFromDead) {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "doActionAfterConnection()",
          "OnComeBackOnline EMISSION !!!!"
        );
        this._eventService.OnComeBackOnline.next(null);

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "doActionAfterConnection()",
          "OnLoginPlantDid EMISSION !!!!"
        );
        this._eventService.OnLoginPlantDid.next(null);

        this._backFromDead = false;
      }
    }
  }

  /**
   * Handle case there are not avaiable:
   * - It present alert ip not allowed if user couln't access via WAN IP
   * - it present alert disconnection if no connection are avaiable //
   */
  private async handleNoAvaiableConnection(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleNoAvaiableConnection()",
      "No available connections. Attempt n.: " + this._disconnectionCountAttemps
    );

    this._disconnectionCountAttemps++;

    if (this._isUserAuthenticated) {
      /** Used insted of Wan connection block  */
      if (this._plantToTest.AllowWanConnection) {
        /** Skip case when count is 1 because it's network get up immediatly it not show popup */
        if (this._disconnectionCountAttemps > 20) {
          this.closeLoaderReconnectionInProgress();

          //Too late for second wind!
          // this._backFromDead = false;

          await this._alertService.presentAlertDisconnection();
        } else if (this._disconnectionCountAttemps >= 2) {
          this._backFromDead = true;

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "handleNoAvaiableConnection()",
            "resetNoConnectionAvaiableVariable 1"
          );
          this.resetNoConnectionAvaiableVariable();

          await this._toastService.showToastConnectionIssues(
            ToastConnectionCodes.API_CONNECTION_LOST
          );
        }
      } else {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleNoAvaiableConnection()",
          "resetNoConnectionAvaiableVariable 2"
        );
        this.resetNoConnectionAvaiableVariable();

        /* NOTE
        This invocation of the method is done only in order to close the toast in the presence of the "WAN NOT ALLOWED" pop-up.
        IT DOES NOT DISPLAY AN EFFECTIVE RE-ESTABLISHMENT OF MQTT CONNECTION
        */
        this._toastService.closeToastConnectionIssues(
          ToastConnectionCodes.MQTT_CONNECTION_REGAIN
        );

        await this._alertService.presentAlertIpNotAllowed();
      }
    } else {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleNoAvaiableConnection()",
        "User is NOT authenticated"
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleNoAvaiableConnection()",
        "resetNoConnectionAvaiableVariable 3"
      );

      this.resetNoConnectionAvaiableVariable();
    }
  }

  /**
   * Changes of the network connection reported by the device, inside the Angular zone.
   *
   * @returns an Observable that emits the new status at every change
   */
  private networkStatusChanges(): Observable<ConnectionStatus> {
    return new Observable<ConnectionStatus>((subscriber) => {
      const listener = Network.addListener("networkStatusChange", (status: ConnectionStatus) =>
        this._ngZone.run(() => subscriber.next(status))
      );

      return () => {
        void listener.then((handle) => handle.remove());
      };
    });
  }

  /**
   * Handle network change on mobile platform.
   * Debounce time is necessary because it's suggested by the library
   */
  private attachMobileNetworkEvent(): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "attachMobileNetworkEvent()",
      "START"
    );

    this.networkStatusChanges()
      .pipe(debounceTime(3000))
      .subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_network - onChange()",
          "attachMobileNetworkEvent()",
          "Do NOT react to plant changes IN MOBILE? " +
          this._plantToTest?.changesBlocked
        );
        //React to changes to connection type (on MOBILE) only if
        //  *A "current plant" is specified
        //  *The flag "changesBlocked" of that plant is set to FALSE
        //  *The operative mode the app runs by while logged in to that plant IS NOT "ACCESS_CONTROL_NODE"
        //
        //This means this code is NOT executed in:
        //  -home.page
        //  -plant.id page
        if (this._plantToTest && !this._plantToTest?.changesBlocked) {
          switch (this._plantToTest.operativeMode) {
            case BIGDeviceOperationMode.ACCESS_CONTROL_MODE:
              //DO NOTHING.
              break;

            case BIGDeviceOperationMode.DIGITAL_PLACE_MODE:
              this.handleDigitalPlaceCheckConnectionProcedure();

              break;

            case BIGDeviceOperationMode.STANDARD:
            default:
              if (this._isPollingcycle0completed) {
                this.startTimerHeartbitHandling();
              }

              break;
          }
        }
      });
  }

  /**
   * Evaluate and optimize full list of avaiable connection address
   */
  private populateAddressToCheck() {
    this._listAddressCheck = [];

    if (
      this._plantToTest.FullLanAddress !== "" ||
      this._plantToTest.FullWanAddress !== ""
    ) {
      if (
        this._plantToTest.FullLanAddress.toUpperCase() ===
        this._plantToTest.FullWanAddress.toUpperCase() ||
        !this._plantToTest.AllowWanConnection
      ) {
        const addressWANLANCheck: NetworkAddressCheck = {
          address: this._plantToTest.FullLanAddress,
          enable: true,
          isOnline: false,
          connectionType: ConnectionType.LANWAN,
        };

        this._listAddressCheck.push(addressWANLANCheck);
      } else {
        if (this._plantToTest.FullLanAddress.toUpperCase() !== "") {
          const addressLanToCheck: NetworkAddressCheck = {
            address: this._plantToTest.FullLanAddress,
            enable: true,
            isOnline: false,
            connectionType: ConnectionType.LAN,
          };

          this._listAddressCheck.push(addressLanToCheck);
        }

        /**toggle button value track if current plant may connect to wan address:
         * it check if wan address is enable to add in avaiable ip address
         * */
        if (
          this._plantToTest.FullWanAddress.toUpperCase() !== "" &&
          this._plantToTest.AllowWanConnection
        ) {
          const addressWanToCheck: NetworkAddressCheck = {
            address: this._plantToTest.FullWanAddress,
            enable: true,
            isOnline: false,
            connectionType: ConnectionType.WAN,
          };

          this._listAddressCheck.push(addressWanToCheck);
        }
      }
    }
  }

  /**
   * Check if current device is platform
   *
   * @returns Return boolean value to check mobile
   */
  private isMobileDevice(): boolean {
    return this._platform.is("ios") || this._platform.is("android");
  }

  /**
   * Set current connection type
   *
   * @param connectionType Connection type to set
   */
  private setCurrentConnectionType(connectionType: ConnectionType): void {
    this._ngZone.run(() => {
      this._currentConnectionType = connectionType;
      this._currentConnectionTypeSubject.next(connectionType);
    });
  }

  /**
   * Get current timeout value by address type.
   * If current environemnt http address is lan return lan timeout,
   * owtherwise wan timeout
   *
   * @returns TImeout value
   */
  private getCurrentEnvironmentAddressTimeout(): number {
    let currentTimeout = AppSettings.DEFAULT_LAN_TIMEOUT_MS;
    // if (this._themeService.applicationTheme != ApplicationTheme.DIGITAL_PLACE) {
    //   const isWanLanConnectionAvaiable =
    //     this._listAddressCheck.find(
    //       (connection: NetworkAddressCheck) =>
    //         connection.connectionType === ConnectionType.LANWAN
    //     ) !== undefined;

    //   if (
    //     isWanLanConnectionAvaiable ||
    //     environment.httpServerRoot === this._plantToTest.FullWanAddress
    //   ) {
    //     currentTimeout = AppSettings.DEFAULT_WAN_TIMEOUT_MS;
    //   }
    // }

    return currentTimeout;
  }

  /**
   * Close the loader associated with the checking of available connections, if it is currently presented
   */
  private closeLoaderReconnectionInProgress(): void {
    this._toastService.closeToastConnectionIssues(
      ToastConnectionCodes.API_CONNECTION_REGAIN
    );
  }

  /**
   * Reset service variable after detect no connections
   */
  private resetNoConnectionAvaiableVariable(): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "resetNoConnectionAvaiableVariable()",
      "Setting httpServerRoot to null and disonnect from MQTT"
    );
    // if (!this._themeService.isDigitalPlace) {
    //   environment.httpServerRoot = "";
    // }

    this._eventService.OnRequestDisconnectMqtt.next(null);

    this.setCurrentConnectionType(ConnectionType.NONE);
  }

  /**
   * Close popup of disconnecction
   */
  private async closePopupDisconnectionHandling(): Promise<void> {
    await this._alertService.clearAlertDisconnection();
    await this._alertService.clearAlertIpNotAllowed();
  }
}
